import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Label,
  TD,
  TH,
  THead,
  TR,
  Table,
  PageHeader,
} from '@nexply/ui';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';

export function AdminPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Admin"
        subtitle="Analytics und Benutzerverwaltung."
      />
      <ActivitySection />
      <AnalyticsSection />
      <AiCostsSection />
      <SignupsSection />
      <UsersSection />
    </div>
  );
}

function deDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function deDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "gerade eben" / "vor 12 Min." / "vor 3 Std." — ab gestern das volle Datum. */
function relTime(iso: string | null): string {
  if (!iso) return 'noch nie';
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 2) return 'gerade eben';
  if (min < 60) return `vor ${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std.`;
  return deDateTime(iso);
}

/** Felder können Kommas, Anführungszeichen und Umbrüche enthalten — immer quoten. */
function csvCell(v: string | number | null): string {
  const s = v === null ? '' : String(v);
  return `"${s.replace(/"/g, '""')}"`;
}

function SignupsSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'signups'],
    queryFn: () => api.admin.listSignups(),
  });

  function exportCsv() {
    if (!data) return;
    const head = [
      'Name',
      'E-Mail',
      'Firma',
      'Eingetragen bei',
      'Zustimmung am',
      'Newsletter',
      'Newsletter am',
      'Eigenes Konto',
      'Eingetragen am',
    ];
    const rows = data.entries.map((e) =>
      [
        e.name,
        e.email,
        e.company,
        e.hostLabel,
        deDate(e.termsAcceptedAt),
        e.newsletterOptIn ? 'ja' : 'nein',
        deDate(e.newsletterOptInAt),
        e.hasAccount ? 'ja' : 'nein',
        deDate(e.createdAt),
      ]
        .map(csvCell)
        .join(';'),
    );
    // BOM voran, sonst zerlegt Excel die Umlaute.
    const blob = new Blob(['﻿' + [head.map(csvCell).join(';'), ...rows].join('\r\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nexply-eintragungen-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Selbst-Eintragungen</CardTitle>
          <Button size="sm" variant="secondary" onClick={exportCsv} disabled={!data?.entries.length}>
            CSV-Export
          </Button>
        </div>
      </CardHeader>
      <CardBody className="pt-0 space-y-4">
        {isLoading && <div className="text-sm text-text-muted">Lädt…</div>}
        {data && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Kpi label="Eintragungen gesamt" value={data.total} />
              <Kpi label="Newsletter-Zusagen" value={data.newsletterCount} />
            </div>

            {data.entries.length === 0 ? (
              <div className="text-sm text-text-muted">
                Noch niemand hat sich über einen Einladungslink eingetragen.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <THead>
                    <TR>
                      <TH>Name</TH>
                      <TH>E-Mail</TH>
                      <TH>Firma</TH>
                      <TH>Eingetragen bei</TH>
                      <TH>Zustimmung</TH>
                      <TH>Newsletter</TH>
                      <TH>Eigenes Konto</TH>
                    </TR>
                  </THead>
                  <tbody>
                    {data.entries.map((e) => (
                      <TR key={e.id}>
                        <TD className="text-text whitespace-nowrap">{e.name}</TD>
                        <TD>{e.email ?? '—'}</TD>
                        <TD>{e.company ?? '—'}</TD>
                        <TD className="whitespace-nowrap">{e.hostLabel}</TD>
                        <TD className="whitespace-nowrap tabular-nums">{deDate(e.termsAcceptedAt)}</TD>
                        <TD className="whitespace-nowrap">
                          {e.newsletterOptIn ? (
                            <span className="inline-flex items-center gap-2">
                              <Badge tone="green">ja</Badge>
                              <span className="text-xs text-text-muted tabular-nums">
                                {deDate(e.newsletterOptInAt)}
                              </span>
                            </span>
                          ) : (
                            <Badge tone="neutral">nein</Badge>
                          )}
                        </TD>
                        <TD>
                          <Badge tone={e.hasAccount ? 'gold' : 'neutral'}>
                            {e.hasAccount ? 'ja' : 'nein'}
                          </Badge>
                        </TD>
                      </TR>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

function ActivitySection() {
  const [days, setDays] = useState(14);
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'activity', days],
    queryFn: () => api.analytics.activity(days),
    // "Jetzt online" soll leben — im Hintergrund regelmäßig nachladen.
    refetchInterval: 30_000,
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Aktivität</CardTitle>
          <div className="inline-flex gap-1">
            {[7, 14, 30].map((d) => (
              <Button
                key={d}
                size="sm"
                variant={days === d ? 'primary' : 'ghost'}
                onClick={() => setDays(d)}
              >
                {d}d
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardBody className="pt-0 space-y-5">
        {isLoading && <div className="text-sm text-text-muted">Lädt…</div>}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi
                label="Jetzt online"
                value={
                  <span className="inline-flex items-center gap-2">
                    <span
                      className={`h-2 w-2 rounded-full ${data.onlineNow > 0 ? 'bg-emerald-500' : 'bg-neutral-500'}`}
                    />
                    {data.onlineNow}
                  </span>
                }
              />
              <Kpi label="Aktiv heute" value={data.activeToday} />
              <Kpi label="Aktiv (7d)" value={data.active7d} />
              <Kpi label="Interaktionen heute" value={data.requestsToday.toLocaleString('de-DE')} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DailyChart
                title="Aktive Nutzer pro Tag"
                days={data.daily.map((d) => ({ date: d.date, value: d.activeUsers }))}
                color="#34d399"
              />
              <DailyChart
                title="Interaktionen pro Tag"
                days={data.daily.map((d) => ({ date: d.date, value: d.requests }))}
                color="#5ea4ff"
              />
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted mb-2">
                Aktivität pro Nutzer ({days} Tage)
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <THead>
                    <TR>
                      <TH>Nutzer</TH>
                      <TH>Zuletzt aktiv</TH>
                      <TH className="text-right">Aktive Tage</TH>
                      <TH className="text-right">Interaktionen</TH>
                    </TR>
                  </THead>
                  <tbody>
                    {data.byUser.map((u) => {
                      const online =
                        u.lastSeenAt !== null && Date.now() - new Date(u.lastSeenAt).getTime() < 5 * 60_000;
                      return (
                        <TR key={u.id}>
                          <TD className="text-text whitespace-nowrap">
                            <span className="inline-flex items-center gap-2">
                              {online && <span className="h-2 w-2 rounded-full bg-emerald-500" />}
                              {u.label}
                            </span>
                            <div className="text-xs text-text-muted">{u.email}</div>
                          </TD>
                          <TD className="whitespace-nowrap tabular-nums">{relTime(u.lastSeenAt)}</TD>
                          <TD className="text-right tabular-nums">
                            {u.activeDays}/{days}
                          </TD>
                          <TD className="text-right tabular-nums">{u.requests.toLocaleString('de-DE')}</TD>
                        </TR>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function usd(n: number): string {
  // Kleine Beträge feiner auflösen, damit einzelne Aufrufe sichtbar bleiben.
  if (n > 0 && n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

function AiCostsSection() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'ai-costs', days],
    queryFn: () => api.analytics.aiCosts(days),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>KI-Kosten</CardTitle>
          <div className="inline-flex gap-1">
            {[7, 30, 90].map((d) => (
              <Button
                key={d}
                size="sm"
                variant={days === d ? 'primary' : 'ghost'}
                onClick={() => setDays(d)}
              >
                {d}d
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardBody className="pt-0 space-y-5">
        {isLoading && <div className="text-sm text-text-muted">Lädt…</div>}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Kosten gesamt" value={usd(data.totals.costUsd)} />
              <Kpi label="Aufrufe" value={data.totals.calls} />
              <Kpi label="Input-Tokens" value={data.totals.inputTokens.toLocaleString('de-DE')} />
              <Kpi label="Output-Tokens" value={data.totals.outputTokens.toLocaleString('de-DE')} />
            </div>

            {data.totals.calls === 0 && (
              <div className="text-sm text-text-muted">
                Noch keine KI-Nutzung im Zeitraum. Kosten erscheinen, sobald KI-Features aktiv sind.
              </div>
            )}

            <DailyChart
              title="Kosten pro Tag (USD)"
              days={data.daily.map((d) => ({ date: d.date, value: d.costUsd }))}
              color="#c9a84c"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CostTable title="Wofür (Feature)" rows={data.byFeature} />
              <CostTable title="Nach Modell" rows={data.byModel} />
            </div>
            <CostTable title="Nach Nutzer" rows={data.byUser} />
          </>
        )}
      </CardBody>
    </Card>
  );
}

function CostTable({
  title,
  rows,
}: {
  title: string;
  rows: { key: string; calls: number; inputTokens: number; outputTokens: number; costUsd: number }[];
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-text-muted mb-2">{title}</div>
      <Table>
        <THead>
          <TR>
            <TH>{title.includes('Nutzer') ? 'Nutzer' : title.includes('Modell') ? 'Modell' : 'Feature'}</TH>
            <TH className="text-right">Aufrufe</TH>
            <TH className="text-right">Tokens</TH>
            <TH className="text-right">Kosten</TH>
          </TR>
        </THead>
        <tbody>
          {rows.length === 0 && (
            <TR>
              <TD colSpan={4} className="text-center text-text-muted py-4">
                —
              </TD>
            </TR>
          )}
          {rows.map((r) => (
            <TR key={r.key}>
              <TD className="text-text">{r.key}</TD>
              <TD className="text-right tabular-nums">{r.calls}</TD>
              <TD className="text-right tabular-nums">
                {(r.inputTokens + r.outputTokens).toLocaleString('de-DE')}
              </TD>
              <TD className="text-right tabular-nums">{usd(r.costUsd)}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function AnalyticsSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'analytics', '7d'],
    queryFn: () => api.analytics.last7Days(),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Analytics – letzte 7 Tage</CardTitle>
      </CardHeader>
      <CardBody className="pt-0 space-y-5">
        {isLoading && <div className="text-sm text-text-muted">Lädt…</div>}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="User gesamt" value={data.userCount} />
              <Kpi label="Neu (7d)" value={data.newUsers} />
              <Kpi label="Aktive (7d)" value={data.activeUsers} />
              <Kpi label="Sessions (7d)" value={data.sessionsCreated} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Totals</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Mitglieder" value={data.totals.members} />
                <Kpi label="Matches" value={data.totals.matches} />
                <Kpi label="Beziehungen" value={data.totals.relationships} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DailyChart
                title="Neue User pro Tag"
                days={data.daily.map((d) => ({ date: d.date, value: d.newUsers }))}
                color="#c9a84c"
              />
              <DailyChart
                title="Sessions pro Tag"
                days={data.daily.map((d) => ({ date: d.date, value: d.sessions }))}
                color="#5ea4ff"
              />
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function Kpi({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-lg border border-line bg-raise px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className="mt-1 text-xl font-semibold text-text tabular-nums">{value}</div>
    </div>
  );
}

function DailyChart({ title, days, color }: { title: string; days: { date: string; value: number }[]; color: string }) {
  const max = Math.max(1, ...days.map((d) => d.value));
  const w = 320;
  const h = 120;
  const pad = { l: 10, r: 10, t: 10, b: 22 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const barW = Math.max(1, innerW / days.length - 4);
  // Bei langen Zeiträumen (30d) nicht jeden Tag beschriften — sonst überlappt alles.
  const labelEvery = Math.max(1, Math.ceil(days.length / 12));

  return (
    <div className="rounded-lg border border-line bg-raise p-3">
      <div className="text-xs text-text-muted mb-2">{title}</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32">
        {days.map((d, i) => {
          const x = pad.l + i * (innerW / days.length) + 2;
          const barH = (d.value / max) * innerH;
          const y = pad.t + innerH - barH;
          const day = d.date.slice(8, 10);
          return (
            <g key={d.date}>
              <rect x={x} y={y} width={barW} height={barH} fill={color} opacity="0.85" rx="2" />
              {i % labelEvery === 0 && (
                <text x={x + barW / 2} y={h - 8} textAnchor="middle" fontSize="9" fill="var(--text-muted)">
                  {day}
                </text>
              )}
              {d.value > 0 && labelEvery === 1 && (
                <text x={x + barW / 2} y={y - 3} textAnchor="middle" fontSize="9" fill="var(--text-dim)">
                  {d.value}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function UsersSection() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.admin.listUsers(),
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Parameters<typeof api.admin.updateUser>[1] }) =>
      api.admin.updateUser(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  const del = useMutation({
    mutationFn: (id: string) => api.admin.deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Benutzer</CardTitle>
      </CardHeader>
      <CardBody className="pt-0">
        {isLoading && <div className="text-sm text-text-muted">Lädt…</div>}
        {data && (
          <div className="overflow-x-auto">
          <Table>
            <THead>
              <TR>
                <TH>E-Mail</TH>
                <TH>Name</TH>
                <TH>Beigetreten</TH>
                <TH>Zuletzt aktiv</TH>
                <TH>Rolle</TH>
                <TH>Status</TH>
                <TH className="text-right">Aktionen</TH>
              </TR>
            </THead>
            <tbody>
              {data.users.map((u) => {
                const isSelf = u.id === me?.id;
                return (
                  <TR key={u.id}>
                    <TD className="text-text">{u.email}</TD>
                    <TD>{u.name ?? '—'}</TD>
                    <TD className="whitespace-nowrap tabular-nums">{deDateTime(u.createdAt)}</TD>
                    <TD className="whitespace-nowrap tabular-nums">{relTime(u.lastSeenAt)}</TD>
                    <TD>
                      <Badge tone={u.role === 'ADMIN' ? 'gold' : 'neutral'}>{u.role}</Badge>
                    </TD>
                    <TD>
                      <Badge tone={u.active ? 'green' : 'red'}>
                        {u.active ? 'aktiv' : 'inaktiv'}
                      </Badge>
                    </TD>
                    <TD className="text-right">
                      <div className="inline-flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={isSelf || update.isPending}
                          onClick={() =>
                            update.mutate({
                              id: u.id,
                              input: { role: u.role === 'ADMIN' ? 'USER' : 'ADMIN' },
                            })
                          }
                        >
                          {u.role === 'ADMIN' ? 'Zu User' : 'Zu Admin'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={isSelf || update.isPending}
                          onClick={() =>
                            update.mutate({ id: u.id, input: { active: !u.active } })
                          }
                        >
                          {u.active ? 'Deaktivieren' : 'Aktivieren'}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={isSelf || del.isPending}
                          onClick={() => {
                            if (confirm(`${u.email} wirklich löschen?`)) del.mutate(u.id);
                          }}
                        >
                          Löschen
                        </Button>
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
